import type { MusicTrack } from './musicTypes';

/**
 * Разбивка фонотеки: исполнители → альбомы → треки. Чистые функции над списком треков —
 * провайдер ничего отдельно не хранит, группы пересчитываются из тегов.
 *
 * <p><b>Ключ — без регистра и лишних пробелов.</b> «Кино», «КИНО» и «Кино » из трёх разных
 * рипов — один исполнитель; подписью служит написание, встреченное первым.
 *
 * <p><b>Источник очереди.</b> Очередь собирается из «списка, в котором нажали трек». У
 * исполнителя и альбома это строки `artist:<ключ>` и `album:<ключ исполнителя>/<ключ альбома>`:
 * так их можно сохранить вместе с остальным состоянием плеера, как id плейлиста.
 */

export type AlbumGroup = {
  /** Источник очереди: `album:…`. */
  source: string;
  /** Пусто — альбом не указан в тегах. */
  name: string;
  trackIds: string[];
  /** Трек, чья обложка представляет альбом: первый с обложкой, иначе просто первый. */
  cover: MusicTrack;
};

export type ArtistGroup = {
  /** Источник очереди: `artist:…`. */
  source: string;
  /** Пусто — исполнитель не указан в тегах. */
  name: string;
  albums: AlbumGroup[];
  trackIds: string[];
  cover: MusicTrack;
};

const ARTIST_PREFIX = 'artist:';
const ALBUM_PREFIX = 'album:';

export const groupKey = (s: string): string => s.normalize('NFC').replace(/\s+/g, ' ').trim().toLocaleLowerCase();

export const artistSource = (artist: string): string => `${ARTIST_PREFIX}${groupKey(artist)}`;
export const albumSource = (artist: string, album: string): string => `${ALBUM_PREFIX}${groupKey(artist)}/${groupKey(album)}`;

export const isGroupSource = (source: string | null | undefined): source is string =>
  typeof source === 'string' && (source.startsWith(ARTIST_PREFIX) || source.startsWith(ALBUM_PREFIX));

/** Неизвестное — в конец, остальное по алфавиту с учётом цифр («2 альбом» раньше «10 альбома»). */
function compareNames(a: string, b: string): number {
  if (!a !== !b) {
    return a ? -1 : 1;
  }
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

export function groupByArtist(tracks: readonly MusicTrack[]): ArtistGroup[] {
  const artists = new Map<string, { name: string; albums: Map<string, { name: string; tracks: MusicTrack[] }> }>();
  for (const t of tracks) {
    const aKey = groupKey(t.artist);
    let artist = artists.get(aKey);
    if (!artist) {
      artist = { name: t.artist.replace(/\s+/g, ' ').trim(), albums: new Map() };
      artists.set(aKey, artist);
    }
    const alKey = groupKey(t.album);
    let album = artist.albums.get(alKey);
    if (!album) {
      album = { name: t.album.replace(/\s+/g, ' ').trim(), tracks: [] };
      artist.albums.set(alKey, album);
    }
    album.tracks.push(t);
  }
  return [...artists.values()]
    .map((a) => {
      const albums = [...a.albums.values()]
        .map<AlbumGroup>((al) => ({
          source: albumSource(a.name, al.name),
          name: al.name,
          trackIds: al.tracks.map((t) => t.id),
          cover: al.tracks.find((t) => t.artworkRef) ?? al.tracks[0],
        }))
        .sort((x, y) => compareNames(x.name, y.name));
      return {
        source: artistSource(a.name),
        name: a.name,
        albums,
        trackIds: albums.flatMap((al) => al.trackIds),
        cover: albums.find((al) => al.cover.artworkRef)?.cover ?? albums[0].cover,
      };
    })
    .sort((x, y) => compareNames(x.name, y.name));
}

/** Треки исполнителя или альбома по источнику очереди; `null` — это не группа. */
export function groupTrackIds(source: string, tracks: readonly MusicTrack[]): string[] | null {
  if (!isGroupSource(source)) {
    return null;
  }
  for (const artist of groupByArtist(tracks)) {
    if (artist.source === source) {
      return artist.trackIds;
    }
    const album = artist.albums.find((al) => al.source === source);
    if (album) {
      return album.trackIds;
    }
  }
  return [];
}

/** Исполнитель и альбом по источнику — для заголовка списка и кнопки «назад». */
export function findGroup(
  source: string,
  artists: readonly ArtistGroup[],
): { artist: ArtistGroup; album: AlbumGroup | null } | null {
  for (const artist of artists) {
    if (artist.source === source) {
      return { artist, album: null };
    }
    const album = artist.albums.find((al) => al.source === source);
    if (album) {
      return { artist, album };
    }
  }
  return null;
}

export const TRACK_FIELD_MAX = 120;

export function cleanTrackField(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim().slice(0, TRACK_FIELD_MAX);
}

/** Правка сведений о треке. Не переданное поле не трогается; пустое название не принимается. */
export type TrackEdit = { title?: string; artist?: string; album?: string };

export function applyTrackEdit<T extends { title: string; artist: string; album: string }>(t: T, edit: TrackEdit): T {
  const title = edit.title === undefined ? t.title : cleanTrackField(edit.title) || t.title;
  const artist = edit.artist === undefined ? t.artist : cleanTrackField(edit.artist);
  const album = edit.album === undefined ? t.album : cleanTrackField(edit.album);
  return title === t.title && artist === t.artist && album === t.album ? t : { ...t, title, artist, album };
}
